/**
 * Vault read — one Lambda (vault-read) handling both listing and revealing,
 * since both are "per-caller filtered read" concerns. Replaces what used to
 * be two separate Lambdas (vault-list, vault-reveal); merged to cut down on
 * Lambda/IAM-role count in the backend.
 *
 * vaultList: visibility is per-caller — admins get every credential
 * (my_level: 'admin'), everyone else only gets credentials they hold an
 * active grant on (my_level: 'viewer' | 'revealer').
 *
 * vaultReveal: decrypts and returns exactly one credential's password.
 * Grant-gated server-side (admin, or an active 'revealer' grant on that
 * credential) — any admin-only check in the UI is UX only. Writes the
 * `credential_revealed` row to vault_activity_log inside the same
 * invocation, so a reveal can never happen without being logged.
 *
 * vaultListArchived: admin-only — returns archived credentials (excluded
 * from the normal `list` action) so admins have somewhere to see and
 * restore them. Requires moreece-vault to support a `list_archived` action
 * — see VAULT-BACKEND-HANDOFF.md, not confirmed deployed yet.
 *
 * Usage:
 *   const { data } = await vaultList(session);
 *   const { password } = await vaultReveal(session, credentialId);
 *   const { data } = await vaultListArchived(session);
 */

const FUNC_URL = import.meta.env.VITE_AWS_VAULT_READ_URL;

async function callVaultRead(body) {
  if (!FUNC_URL) {
    throw new Error(
      '[vaultRead] VITE_AWS_VAULT_READ_URL is not set. ' +
      'The Vault backend (Lambda + migration, in moreece-aws) has not been deployed yet — see app/.env.example.'
    );
  }
  const res = await fetch(FUNC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.error || `Vault read failed: ${res.status}`);
  }
  return json;
}

export function vaultList(session) {
  return callVaultRead({ pin: session?.code, action: 'list' });
}

export function vaultReveal(session, credentialId) {
  return callVaultRead({ pin: session?.code, action: 'reveal', credential_id: credentialId });
}

export function vaultListArchived(session) {
  return callVaultRead({ pin: session?.code, action: 'list_archived' });
}
